<template>
    <v-app-bar color="#f1f2f3" app flat height="66">
        <v-card flat color="transparent" class="w-100 d-flex mx-2 mt-3 align-center">
            <template v-if="showBreadcrumbs">
                <v-breadcrumbs :items="breadcrumbItems" class="appbar-breadcrumbs ml-2">
                    <template v-slot:divider>
                        <v-icon>mdi-chevron-right</v-icon>
                    </template>
                    <template v-slot:item="{ item }">
                        <div class="d-flex flex-column px-2">
                            <span class="caption text-uppercase text--disabled">{{ item.level }}</span>
                            <span :class="{ 'font-weight-bold': item.isLastWithValue, 'text--disabled': item.muted }">{{ item.text }}</span>
                        </div>
                    </template>
                </v-breadcrumbs>
            </template>
            <v-spacer />

            <v-tooltip bottom v-if="displayAPIWarning">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on">
                        <v-icon color="red">mdi-alert</v-icon>
                    </v-btn>
                </template>
                <span>You are connected to {{ customBaseURL }}</span>
            </v-tooltip>

            <div v-if="$auth.isAuthenticated" class="d-flex align-center justify-space-around flex-shrink-0">
                <template v-if="userInfo && userInfo.is_trial_user">
                    <v-chip label>
                        {{ trialText }}
                        <v-btn
                            v-if="$appConfig.VUE_APP_INTERCOM_ID"
                            elevation="0"
                            x-small
                            class="ml-5"
                            color="primary"
                            @click="showNewMessageIntercom(trialMsgIntercom)">
                            {{ trialButtonLabel }}
                        </v-btn>
                    </v-chip>
                </template>

                <ToolbarRunningApps />
                <UserNotificationsMenu />
            </div>
        </v-card>
    </v-app-bar>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { intercomMethods } from '@/mixins/intercom'
import { calculateDateDifferenceInDays } from '../utils'

const ToolbarRunningApps = () => import('@/components/ToolbarRunningApps.vue')
const UserNotificationsMenu = () => import('@/components/UserNotificationsMenu.vue')

export default {
    name: 'AppBar',
    mixins: [enumsData, intercomMethods],
    components: {
        ToolbarRunningApps,
        UserNotificationsMenu
    },
    data() {
        return {
            customBaseURL: window.localStorage.getItem('nvBaseUrl')
        }
    },
    methods: {},
    computed: {
        ...mapState(['userInfo', 'trialInfo', 'userOrgs', 'currentOrgData']),
        ...mapState('spaceStore', ['masterInstance']),
        ...mapGetters('orgStore', ['orgLongName']),
        ...mapGetters('spaceStore', ['currentSpaceName', 'isSpaceArchived', 'isSpaceRested']),
        ...mapGetters('instanceStore', ['instanceLongNameById']),
        ...mapGetters('snapshotStore', ['currentSnapshotName', 'snapshotLongNameById', 'isDevelopment']),
        selectedOrgName() {
            if (!this.$route.params.oid) {
                return null
            }
            if (this.orgLongName) {
                return this.orgLongName
            }
            const selectedOrg = this.userOrgs.find(org => org.oid.toString() === this.$route.params.oid.toString())
            if (selectedOrg?.long_id) {
                return selectedOrg.long_id
            }
            return this.currentOrgData?.long_id || `Organization ${this.$route.params.oid}`
        },
        selectedSpaceName() {
            if (!this.$route.params.sid) {
                return null
            }
            return this.currentSpaceName
        },
        selectedInstanceName() {
            if (!this.$route.params.sid) {
                return null
            }
            const isConfigRoute = (this.$route.name || '').startsWith('config')
            if (this.$route.params.iid) {
                return this.instanceLongNameById(this.$route.params.iid) || `Instance ${this.$route.params.iid}`
            }
            if (isConfigRoute) {
                return null
            }
            return this.masterInstance?.long_id || 'Master'
        },
        selectedSnapshotName() {
            if (this.isSpaceArchived && this.isDevelopment) {
                return 'Archived'
            }
            if (this.isSpaceRested && this.isDevelopment) {
                return 'Resting'
            }

            const snid = this.$route.params.snid
            if (!snid) {
                return null
            }

            const resolvedName = this.snapshotLongNameById(snid) || this.currentSnapshotName
            if (resolvedName) {
                return resolvedName
            }

            return `Snapshot ${snid}`
        },
        isInactiveSpace() {
            return Boolean(this.isSpaceArchived || this.isSpaceRested)
        },
        breadcrumbItems() {
            const items = [
                { level: 'organization', value: this.selectedOrgName, disabled: true, muted: false },
                { level: 'space', value: this.selectedSpaceName, disabled: true, muted: this.isInactiveSpace },
                { level: 'instance', value: this.selectedInstanceName, disabled: true, muted: this.isInactiveSpace },
                { level: 'snapshot', value: this.selectedSnapshotName, disabled: true, muted: this.isInactiveSpace }
            ]

            const lastValueIndex = items.reduce((lastIndex, item, index) => {
                return item.value ? index : lastIndex
            }, -1)

            return items.map((item, index) => ({
                ...item,
                text: item.value || '-',
                isLastWithValue: index === lastValueIndex
            }))
        },
        trialText() {
            if (this.trialInfo && this.trialInfo.valid) {
                return `Trial period: ${this.trialDaysLeft} days left.`
            }
            if (this.trialInfo && !this.trialInfo.valid) {
                return `Your trial is up!`
            }
            return ''
        },
        trialButtonLabel() {
            if (this.trialInfo && this.trialInfo.tier === this.trialTiers.FREE) {
                return 'Upgrade'
            } else {
                return 'Convert'
            }
        },
        trialMsgIntercom() {
            if (this.trialInfo && this.trialInfo.tier === this.trialTiers.FREE) {
                return 'Hi Nuvolos Team,\n\nI would be interested in upgrading my trial account to test the complete Nuvolos experience.'
            } else {
                return 'Hi Nuvolos Team,\n\nI would like to convert my trial account to a regular plan account.'
            }
        },
        trialDaysLeft() {
            if (this.trialInfo && this.trialInfo.end_date) {
                return calculateDateDifferenceInDays(Date.now(), this.trialInfo.end_date)
            }
            return null
        },
        displayAPIWarning() {
            return this.$appConfig.VUE_APP_ENV !== 'production' && this.customBaseURL
        },
        showBreadcrumbs() {
            return this.breadcrumbItems.length > 0 && !(this.$route.name || '').includes('reporting')
        },
        showLogo() {
            return !this.$route.params.sid || (this.$route.name || '').includes('reporting')
        }
    }
}
</script>

<style scoped lang="scss">
.appbar-breadcrumbs {
    ::v-deep .v-breadcrumbs__divider {
        display: inline-flex;
        align-items: flex-end;
        align-self: flex-end;
        line-height: 1;
        padding-bottom: 2px;
    }
}
</style>
